import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ApolloProvider } from '@apollo/client/react'
import { Loader2 } from 'lucide-react'
import { apolloClient } from './graphql/client'
import { AuthProvider } from './contexts/AuthContext'
import { AlertProvider } from './contexts/AlertContext'
import PrivateRoute from './components/common/PrivateRoute'
import { ErrorBoundaryWrapper } from './components/common/ErrorBoundary'
import { GlobalAlert } from './components/common/GlobalAlert'
import './i18n'

const AppLayout = lazy(() => import('./components/layout/AppLayout'))
const Login = lazy(() => import('./pages/Login'))
const SetPassword = lazy(() => import('./pages/auth/SetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const TodoListPage = lazy(() => import('./pages/TodoListPage'))
const Statistics = lazy(() => import('./pages/dashboard/Statistics'))
const MerchantList = lazy(() => import('./pages/merchant/MerchantList'))
const MerchantDetail = lazy(() => import('./pages/merchant/MerchantDetail'))
const MerchantPaymentMethodList = lazy(
  () => import('./pages/merchant/MerchantPaymentMethodList')
)
const MerchantPaymentMethodDetail = lazy(
  () => import('./pages/merchant/MerchantPaymentMethodDetail')
)
const MyCryptoRefundFeeList = lazy(
  () => import('./pages/merchant/MyCryptoRefundFeeList')
)
const MerchantSettingsPage = lazy(
  () => import('./pages/merchant/MerchantSettingsPage')
)
const MerchantSecurityPage = lazy(
  () => import('./pages/merchant/MerchantSecurityPage')
)
const PaymentList = lazy(() => import('./pages/payment/PaymentList'))
const TopupAddressList = lazy(() => import('./pages/payment/TopupAddressList'))
const TopupAddressDetail = lazy(
  () => import('./pages/payment/TopupAddressDetail')
)
const PaymentDetail = lazy(() => import('./pages/payment/PaymentDetail'))
const ManualRefundApprovalDetail = lazy(
  () => import('./pages/payment/ManualRefundApprovalDetail')
)
const ExceptionalTransferDetail = lazy(
  () => import('./pages/payment/ExceptionalTransferDetail')
)
const RefundList = lazy(() => import('./pages/refund/RefundList'))
const RefundDetail = lazy(() => import('./pages/refund/RefundDetail'))
const AutoRefundList = lazy(() => import('./pages/autoRefund/AutoRefundList'))
const AutoRefundDetail = lazy(
  () => import('./pages/autoRefund/AutoRefundDetail')
)
const SettlementList = lazy(() => import('./pages/settlement/SettlementList'))
const SettlementDetail = lazy(
  () => import('./pages/settlement/SettlementDetail')
)
const MenuManagement = lazy(() => import('./pages/system/MenuManagement'))
const PgSettlementCreate = lazy(
  () => import('./pages/system/PgSettlementCreate')
)
const PgSettlementDetail = lazy(
  () => import('./pages/system/PgSettlementDetail')
)
const PgSettlementTransferDetail = lazy(
  () => import('./pages/system/PgSettlementTransferDetail')
)
const ReconciliationRunDetail = lazy(
  () => import('./pages/system/ReconciliationRunDetail')
)
const WithdrawalListPage = lazy(
  () => import('./pages/account/WithdrawalListPage')
)
const WithdrawalRequestPage = lazy(
  () => import('./pages/account/WithdrawalRequestPage')
)
const BeneficiaryListPage = lazy(
  () => import('./pages/account/BeneficiaryListPage')
)
const BeneficiaryCreatePage = lazy(
  () => import('./pages/account/BeneficiaryCreatePage')
)
const WithdrawalDetailPage = lazy(
  () => import('./pages/account/WithdrawalDetailPage')
)
const WithdrawalEditPage = lazy(
  () => import('./pages/account/WithdrawalEditPage')
)
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const MerchantUserList = lazy(() => import('./pages/user/MerchantUserList'))
const MerchantUserDetail = lazy(() => import('./pages/user/MerchantUserDetail'))
const ApiKeys = lazy(() => import('./pages/settings/ApiKeys'))
const WebhookEventList = lazy(
  () => import('./pages/webhookEvent/WebhookEventList')
)
const WebhookEventDetail = lazy(
  () => import('./pages/webhookEvent/WebhookEventDetail')
)
const AccountPage = lazy(() => import('./pages/account/AccountPage'))
const SystemSettingsPage = lazy(
  () => import('./pages/system/SystemSettingsPage')
)
const TopupPage = lazy(() => import('./pages/account/TopupPage'))
const TopupDetailPage = lazy(() => import('./pages/account/TopupDetailPage'))
const OnrampPage = lazy(() => import('./pages/fiatTrading/OnrampPage'))
const OnrampDetailPage = lazy(
  () => import('./pages/fiatTrading/OnrampDetailPage')
)
const PayoutDetailPage = lazy(
  () => import('./pages/transaction/PayoutDetailPage')
)
const PayoutPage = lazy(() => import('./pages/transaction/PayoutPage'))
const InternalAccountPage = lazy(
  () => import('./pages/system/InternalAccountPage')
)
const CurrencyExchangePage = lazy(
  () => import('./pages/system/CurrencyExchangePage')
)
const PaymentInfraPage = lazy(() => import('./pages/system/PaymentInfraPage'))
const ChannelInfraPage = lazy(() => import('./pages/system/ChannelInfraPage'))
const FeeManagementPage = lazy(() => import('./pages/system/FeeManagementPage'))
const PgSettlementPage = lazy(() => import('./pages/system/PgSettlementPage'))
const OperationsPage = lazy(() => import('./pages/system/OperationsPage'))
const OfframpTransferCreate = lazy(
  () => import('./pages/system/channel-transfer/OfframpTransferCreate')
)
const ManualTransferCreate = lazy(
  () => import('./pages/system/channel-transfer/ManualTransferCreate')
)
const ChannelTransferDetail = lazy(
  () => import('./pages/system/channel-transfer/ChannelTransferDetail')
)
const OfframpPage = lazy(() => import('./pages/fiatTrading/OfframpPage'))
const OfframpDetailPage = lazy(
  () => import('./pages/fiatTrading/OfframpDetailPage')
)
const AmlAlertDetail = lazy(() => import('./pages/system/AmlAlertDetail'))
const LogsPage = lazy(() => import('./pages/system/LogsPage'))
const AmlScreeningResultDetail = lazy(
  () => import('./pages/system/AmlScreeningResultDetail')
)
const TwoFactorSetupPage = lazy(
  () => import('./pages/security/TwoFactorSetupPage')
)
const SecurityPolicyPage = lazy(
  () => import('./pages/admin/SecurityPolicyPage')
)

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Loader2 className="w-8 h-8 animate-spin" />
  </div>
)

function App() {
  return (
    <ErrorBoundaryWrapper>
      <Suspense fallback={<LoadingFallback />}>
        <ApolloProvider client={apolloClient}>
          <AuthProvider>
            <AlertProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/set-password" element={<SetPassword />} />
                  <Route element={<PrivateRoute />}>
                    {/* 강제 2FA 등록 페이지: AppLayout(사이드바/me 쿼리) 없이 전체 화면.
                        서버가 미등록 사용자의 일반 op 를 차단하므로 셸 밖에 둔다. */}
                    <Route
                      path="/security/2fa-setup"
                      element={<TwoFactorSetupPage />}
                    />
                    <Route element={<AppLayout />}>
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/todo" element={<TodoListPage />} />
                      <Route
                        path="/dashboard/statistics"
                        element={<Statistics />}
                      />
                      <Route
                        path="/merchant/merchants"
                        element={<MerchantList />}
                      />
                      <Route
                        path="/merchant/my-merchant"
                        element={<MerchantDetail />}
                      />
                      <Route
                        path="/merchant/merchants/new"
                        element={<MerchantDetail />}
                      />
                      <Route
                        path="/merchant/merchants/:id"
                        element={<MerchantDetail />}
                      />
                      <Route
                        path="/merchant/merchant-payment-methods"
                        element={<MerchantPaymentMethodList />}
                      />
                      <Route
                        path="/merchant/merchant-payment-methods/:id"
                        element={<MerchantPaymentMethodDetail />}
                      />
                      <Route
                        path="/merchant/my-crypto-refund-fees"
                        element={<MyCryptoRefundFeeList />}
                      />
                      <Route
                        path="/merchant/my-settings"
                        element={<MerchantSettingsPage />}
                      />
                      <Route
                        path="/merchant/my-security"
                        element={<MerchantSecurityPage />}
                      />
                      <Route
                        path="/payment/payments"
                        element={<PaymentList />}
                      />
                      <Route
                        path="/payment/payments/:id"
                        element={<PaymentDetail />}
                      />
                      <Route path="/payment/refunds" element={<RefundList />} />
                      <Route
                        path="/payment/refunds/:id"
                        element={<RefundDetail />}
                      />
                      <Route
                        path="/payment/topup-addresses"
                        element={<TopupAddressList />}
                      />
                      <Route
                        path="/payment/topup-addresses/:customId"
                        element={<TopupAddressDetail />}
                      />
                      <Route
                        path="/account/topup-addresses"
                        element={
                          <Navigate to="/payment/topup-addresses" replace />
                        }
                      />
                      <Route
                        path="/payment/auto-refunds"
                        element={<AutoRefundList />}
                      />
                      <Route
                        path="/payment/auto-refunds/:id"
                        element={<AutoRefundDetail />}
                      />
                      <Route
                        path="/system/exceptional-transfers/:id"
                        element={<ExceptionalTransferDetail />}
                      />
                      {/* 통합 메뉴: 통화/환율 관리 */}
                      <Route
                        path="/system/currency-exchange"
                        element={<CurrencyExchangePage />}
                      />
                      <Route
                        path="/system/menus"
                        element={<MenuManagement />}
                      />
                      <Route
                        path="/system/channels"
                        element={<ChannelInfraPage />}
                      />
                      <Route
                        path="/system/channel-funds"
                        element={
                          <Navigate to="/system/channels?tab=funds" replace />
                        }
                      />
                      {/* 통합 메뉴: 결제 인프라 */}
                      <Route
                        path="/system/payment-infra"
                        element={<PaymentInfraPage />}
                      />
                      {/* 통합 메뉴: 수수료 관리 */}
                      <Route
                        path="/system/fee-management"
                        element={<FeeManagementPage />}
                      />
                      {/* 통합 메뉴: PG 정산 */}
                      <Route
                        path="/system/pg-settlement"
                        element={<PgSettlementPage />}
                      />
                      <Route
                        path="/system/pg-settlement/new"
                        element={<PgSettlementCreate />}
                      />
                      <Route
                        path="/system/pg-settlement/:settlementId"
                        element={<PgSettlementDetail />}
                      />
                      <Route
                        path="/system/pg-settlement/:settlementId/transfers/:transferId"
                        element={<PgSettlementTransferDetail />}
                      />
                      {/* 통합 메뉴: 운영 도구 */}
                      <Route
                        path="/system/operations"
                        element={<OperationsPage />}
                      />
                      <Route
                        path="/system/operations/offramp/new"
                        element={<OfframpTransferCreate />}
                      />
                      <Route
                        path="/system/operations/manual-transfer/new"
                        element={<ManualTransferCreate />}
                      />
                      <Route
                        path="/system/operations/channel-transfer/:id"
                        element={<ChannelTransferDetail />}
                      />
                      <Route
                        path="/system/reconciliation-runs/:id"
                        element={<ReconciliationRunDetail />}
                      />
                      <Route
                        path="/system/manual-refund-approvals/:id"
                        element={<ManualRefundApprovalDetail />}
                      />
                      <Route
                        path="/system/aml-alerts/:id"
                        element={<AmlAlertDetail />}
                      />
                      <Route path="/system/logs" element={<LogsPage />} />
                      <Route
                        path="/system/logs/aml-results/:id"
                        element={<AmlScreeningResultDetail />}
                      />
                      {/* 기존 경로 → 새 경로 리다이렉트 */}
                      <Route
                        path="/system/currencies"
                        element={
                          <Navigate
                            to="/system/currency-exchange?tab=currency"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/exchange-rates"
                        element={
                          <Navigate
                            to="/system/currency-exchange?tab=exchange-rate"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/payment-gateway"
                        element={
                          <Navigate
                            to="/system/payment-infra?tab=gateway"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/payment-routing"
                        element={
                          <Navigate
                            to="/system/payment-infra?tab=routing"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/payment-methods"
                        element={
                          <Navigate
                            to="/system/payment-infra?tab=methods"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/crypto-refund-fees"
                        element={
                          <Navigate
                            to="/system/fee-management?tab=crypto-refund"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/pg-settlements"
                        element={
                          <Navigate
                            to="/system/pg-settlement?tab=list"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/pg-settlements/new"
                        element={<PgSettlementCreate />}
                      />
                      <Route
                        path="/pg-settlements/:settlementId"
                        element={<PgSettlementDetail />}
                      />
                      <Route
                        path="/pg-settlements/:settlementId/transfers/:transferId"
                        element={<PgSettlementTransferDetail />}
                      />
                      <Route
                        path="/pg-settlements/wallet-config"
                        element={
                          <Navigate
                            to="/system/pg-settlement?tab=wallet-config"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/reconciliation-runs"
                        element={
                          <Navigate
                            to="/system/operations?tab=reconciliation"
                            replace
                          />
                        }
                      />
                      <Route
                        path="/system/manual-refund-approvals"
                        element={
                          <Navigate
                            to="/system/operations?tab=manual-refund"
                            replace
                          />
                        }
                      />
                      {/* 가맹점 정산 */}
                      <Route
                        path="/settlement/settlements"
                        element={<SettlementList />}
                      />
                      <Route
                        path="/settlement/settlements/:id"
                        element={<SettlementDetail />}
                      />
                      <Route
                        path="/account/balance"
                        element={<AccountPage />}
                      />
                      <Route
                        path="/account/withdrawals"
                        element={<WithdrawalListPage />}
                      />
                      <Route
                        path="/account/withdrawal/request"
                        element={<WithdrawalRequestPage />}
                      />
                      <Route
                        path="/account/beneficiaries"
                        element={<BeneficiaryListPage />}
                      />
                      <Route
                        path="/account/beneficiaries/new"
                        element={<BeneficiaryCreatePage />}
                      />
                      <Route
                        path="/account/withdrawal/:id"
                        element={<WithdrawalDetailPage />}
                      />
                      <Route
                        path="/account/withdrawal/:id/edit"
                        element={<WithdrawalEditPage />}
                      />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route
                        path="/system/security-policy"
                        element={<SecurityPolicyPage />}
                      />
                      <Route
                        path="/user/merchant-users"
                        element={<MerchantUserList />}
                      />
                      <Route
                        path="/user/merchant-users/new"
                        element={<MerchantUserDetail />}
                      />
                      <Route
                        path="/user/merchant-users/:id"
                        element={<MerchantUserDetail />}
                      />
                      <Route path="/settings/api-keys" element={<ApiKeys />} />

                      <Route
                        path="/settings/webhook-events"
                        element={<WebhookEventList />}
                      />
                      <Route
                        path="/settings/webhook-events/:id"
                        element={<WebhookEventDetail />}
                      />
                      <Route
                        path="/system/settings"
                        element={<SystemSettingsPage />}
                      />
                      <Route
                        path="/system/fee-settings"
                        element={
                          <Navigate to="/system/fee-management" replace />
                        }
                      />
                      <Route path="/account/topups" element={<TopupPage />} />
                      <Route
                        path="/account/topups/:orderId"
                        element={<TopupDetailPage />}
                      />
                      <Route
                        path="/fiat-trading/onramps"
                        element={<OnrampPage />}
                      />
                      <Route
                        path="/fiat-trading/onramps/:orderId"
                        element={<OnrampDetailPage />}
                      />
                      <Route
                        path="/fiat-trading/offramps"
                        element={<OfframpPage />}
                      />
                      <Route
                        path="/fiat-trading/offramps/:orderId"
                        element={<OfframpDetailPage />}
                      />
                      <Route
                        path="/transaction/payouts"
                        element={<PayoutPage />}
                      />
                      <Route
                        path="/transaction/payouts/:orderId"
                        element={<PayoutDetailPage />}
                      />
                      <Route
                        path="/system/internal-account"
                        element={<InternalAccountPage />}
                      />
                    </Route>
                  </Route>
                </Routes>
                <GlobalAlert />
              </BrowserRouter>
            </AlertProvider>
          </AuthProvider>
        </ApolloProvider>
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}

export default App
